import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { partners as mockPartners } from '@/mocks/partners';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import { Panel, PanelHeader } from '@/components/ui/Panel';
import Toast from '@/components/ui/Toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { onboardingApi, type ListingRow, type ListingsPayload, type PartnerFull } from '@/lib/onboarding';
import { ordersApi, poTone, type OrderRow } from '@/lib/orders';
import { aed, daysBetween, shortDate } from '@/lib/format';
import { btnGhost, btnPrimary, fmtAed } from '../onboarding/components/styles';
import CabinetStep from '../onboarding/components/CabinetStep';
import ContractStep from '../onboarding/components/ContractStep';
import PriceListStep from '../onboarding/components/PriceListStep';
import SitePanel from './SitePanel';

const TABS = ['overview', 'listings', 'site', 'lists', 'terms', 'contract', 'orders'] as const;
type Tab = (typeof TABS)[number];

const toneOfListing = (s: string) => (s === 'live' ? 'primary' : s === 'failed' ? 'danger' : s === 'approved' || s === 'queued' ? 'secondary' : s === 'archived' ? 'neutral' : 'accent');
const toneOfAvail = (s: string) => (s === 'in_stock' ? 'primary' : s === 'low_stock' || s === 'in_transit' ? 'accent' : s === 'discontinued' ? 'neutral' : 'danger');

/** Карточка поставщика на реальных данных (этап 1.3): обзор и готовность к сайту, листинги с ценами каналов,
 *  версии прайса, условия (общая форма с онбордингом), договор, заказы из Zoho. Вкладка — в ?tab=. */
export default function PartnerDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { me } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.includes(params.get('tab') as Tab) ? params.get('tab') : 'overview') as Tab;
  const [partner, setPartner] = useState<PartnerFull | null>(null);
  const [data, setData] = useState<ListingsPayload | null>(null);
  const [orders, setOrders] = useState<OrderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone?: 'success' | 'error' } | null>(null);
  const [busy, setBusy] = useState(false);
  const canEdit = me?.role === 'admin' || me?.role === 'partner_manager';

  const load = useCallback(async () => {
    if (!id) return;
    if (!/^[0-9a-f-]{36}$/i.test(id)) {
      // демо-идентификатор со старых экранов на моках (s1…s13): находим настоящего партнёра по названию
      const mock = mockPartners.find((m) => m.id === id);
      const real = mock ? (await onboardingApi.partners().catch(() => [] as PartnerFull[])).find((p) => p.name.trim().toLowerCase() === mock.name.trim().toLowerCase()) : undefined;
      if (real) navigate(`/partners/${real.id}${window.location.search}`, { replace: true });
      else setError('not_found');
      return;
    }
    try {
      const [p, l] = await Promise.all([onboardingApi.partner(id), onboardingApi.listings(id)]);
      setPartner(p);
      setData(l);
      setError(null);
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, [id, navigate]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!id || tab !== 'orders' || orders) return;
    ordersApi.list({ partnerId: id }).then((d) => setOrders(d.orders)).catch(() => setOrders([]));
  }, [id, tab, orders]);

  const setTab = (s: Tab) => {
    const q = new URLSearchParams(params);
    q.set('tab', s);
    setParams(q, { replace: true });
  };
  const onToast = (message: string, tone: 'success' | 'error' = 'success') => setToast({ message, tone });
  const onPartner = (p: PartnerFull) => {
    setPartner(p);
    void load();
  };
  const rows = useMemo(() => data?.rows.filter((r) => r.productStatus !== 'Discontinued') ?? [], [data]);

  if (error) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        <p className="text-sm font-medium text-foreground-900">{error === 'not_found' ? t('pd.notFound') : t('onb.error', { code: error })}</p>
        <Link to="/partners" className="mt-2 inline-block text-[12px] font-semibold text-primary-700">{t('pd.backToPartners')}</Link>
      </div>
    );
  }
  if (!partner || !data) return <div className="p-8 text-center text-[12.5px] text-foreground-500">…</div>;

  const live = rows.filter((r) => r.listedOnSite).length;
  const withImages = rows.filter((r) => r.images.length > 0).length;
  const today = new Date().toISOString().slice(0, 10);
  const staleDays = partner.lastPriceListAt ? daysBetween(partner.lastPriceListAt.slice(0, 10), today) : null;
  const statusTone = partner.status === 'Active' ? 'primary' : partner.status === 'Paused' ? 'danger' : 'accent';
  const contractTone = partner.contractStatus === 'Signed' ? 'primary' : partner.contractStatus === 'Expired' ? 'danger' : 'accent';
  const phone = partner.whatsapp || partner.phone;
  const paused = partner.status === 'Paused';
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

  const togglePause = async () => {
    setBusy(true);
    try {
      const p = await onboardingApi.patchPartner(partner.id, { status: paused ? 'Active' : 'Paused' });
      onPartner(p);
      onToast(paused ? t('pd.resumeToast') : t('pd.pauseToast'));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };
  const readiness: { key: string; ok: boolean; value: string; to: string }[] = [
    { key: 'cabinet', ok: Boolean(partner.trn && partner.warehouse), value: partner.trn ? t('pc.filled') : t('pc.noTrn'), to: `/partners/${partner.id}/onboarding?step=cabinet` },
    { key: 'pricelist', ok: rows.length > 0, value: partner.lastPriceListAt ? `v${partner.lastPriceListVersion} · ${shortDate(partner.lastPriceListAt)}` : t('ov.noList'), to: `/partners/${partner.id}/onboarding?step=pricelist` },
    { key: 'preview', ok: withImages > 0 && data.summary.ready > 0, value: t('pc.readyOf', { ready: data.summary.ready, total: rows.length, images: withImages }), to: `/partners/${partner.id}/onboarding?step=preview` },
    { key: 'approval', ok: Boolean(data.summary.approval), value: data.summary.approval ? `${data.summary.approval.approvedByName} · ${shortDate(data.summary.approval.approvedAt)}` : t('onb.no'), to: `/partners/${partner.id}/onboarding?step=approval` },
    { key: 'contract', ok: data.summary.contractOk, value: t(`status.${partner.contractStatus}`, partner.contractStatus), to: `/partners/${partner.id}/onboarding?step=contract` },
    { key: 'send', ok: live > 0, value: t('pc.liveOf', { live, total: rows.length }), to: `/partners/${partner.id}/onboarding?step=send` },
  ];

  const field = (label: string, value: string | number | null | undefined) => (
    <div className="min-w-0">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-foreground-400">{label}</p>
      <p className="mt-0.5 break-words text-[12.5px] text-foreground-900">{value == null || value === '' ? '—' : value}</p>
    </div>
  );

  return (
    <div>
      <Link to="/partners" className="mb-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line text-[12px]" /> {t('pd.back')}
      </Link>

      <div className="flex flex-col gap-4 border-b border-background-200 pb-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-secondary-100 text-[13px] font-semibold text-secondary-800">
            {partner.logoPath ? <img src={`/api/partners/${partner.id}/logo`} alt="" className="h-full w-full object-contain" /> : (partner.shortName || partner.name).slice(0, 2).toUpperCase()}
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground-950 md:text-xl">{partner.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <StatusBadge tone={statusTone as 'primary' | 'danger' | 'accent'}>{t(`status.${partner.status}`)}</StatusBadge>
              <StatusBadge tone={contractTone as 'primary' | 'danger' | 'accent'} dot={false}>{t('pd.contract', { status: t(`status.${partner.contractStatus}`) })}</StatusBadge>
              <span className="text-[11.5px] text-foreground-500">{[partner.warehouse, partner.city].filter(Boolean).join(' · ')}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/partners/${partner.id}/onboarding?step=preview`} className={btnPrimary}>
            <i className="ri-layout-grid-line text-[14px]" /> {t('pc.openPreview')}
          </Link>
          <Link to={`/partners/${partner.id}/onboarding`} className={btnGhost}>
            <i className="ri-route-line text-[14px]" /> {t('onb.openOnboarding')}
          </Link>
          <Link to={`/import?partner=${partner.id}`} className={btnGhost}>
            <i className="ri-upload-cloud-2-line text-[14px]" /> {t('partners.importList')}
          </Link>
          {phone && (
            <a href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className={btnGhost}>
              <i className="ri-whatsapp-line text-[14px]" /> {t('pd.message')}
            </a>
          )}
          {canEdit && partner.status !== 'Onboarding' && (
            <button type="button" onClick={togglePause} disabled={busy} className={btnGhost}>
              <i className={`${paused ? 'ri-play-line' : 'ri-pause-line'} text-[14px]`} /> {paused ? t('pd.resume') : t('pd.pause')}
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: t('pd.statSkus'), value: String(rows.length), icon: 'ri-price-tag-3-line' },
          { label: t('pc.statReady'), value: String(data.summary.ready), icon: 'ri-checkbox-circle-line' },
          { label: t('pc.statLive'), value: String(live), icon: 'ri-global-line' },
          { label: t('pd.statLast'), value: staleDays == null ? t('ov.noList') : t('pd.daysAgo', { days: staleDays }), icon: 'ri-time-line' },
        ].map((s) => (
          <div key={s.label} className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div>
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{s.label}</p>
              <p className="tnum mt-1 text-base font-semibold text-foreground-950">{s.value}</p>
            </div>
            <i className={`${s.icon} text-[16px] text-foreground-300`} />
          </div>
        ))}
      </div>

      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-background-200">
        {TABS.map((key) => (
          <button key={key} type="button" onClick={() => setTab(key)} className={`relative cursor-pointer whitespace-nowrap px-3 py-2.5 text-[12.5px] font-medium transition-colors ${tab === key ? 'text-primary-700' : 'text-foreground-500 hover:text-foreground-900'}`}>
            {t(`pc.tabs.${key}`)}
            {tab === key && <span className="absolute inset-x-1 -bottom-px h-[2px] rounded-full bg-primary-500" />}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === 'overview' && (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="flex flex-col gap-4 xl:col-span-2">
              <Panel>
                <PanelHeader title={t('ov.company')} subtitle={t('ov.companySub')} />
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-background-200 pt-3 sm:grid-cols-2 lg:grid-cols-3">
                  {field(t('ov.tradingName'), partner.shortName)}
                  {field(t('ov.contact'), [partner.contact, partner.role].filter(Boolean).join(' · '))}
                  {field(t('ov.phone'), [partner.phone, partner.whatsapp && partner.whatsapp !== partner.phone ? partner.whatsapp : null].filter(Boolean).join(' / '))}
                  {field(t('ov.email'), partner.email)}
                  {field(t('onb.trn'), partner.trn)}
                  {field(t('ov.emirate'), partner.city)}
                  {field(t('ov.warehouse'), partner.warehouse)}
                  {field(t('onb.address'), partner.address)}
                  {field(t('onb.pickupHours'), partner.pickupHours)}
                  {field(t('ov.since'), partner.onboardingStartedAt ? fmt(partner.onboardingStartedAt) : partner.contractStart ? fmt(partner.contractStart) : null)}
                  {field(t('onb.categories'), partner.categories.length ? partner.categories.map((c) => t(`category.${c}`, c)).join(', ') : null)}
                </div>
              </Panel>

              <SitePanel partner={partner} canEdit={Boolean(canEdit)} onPartner={onPartner} onToast={onToast} onApplied={() => void load()} mode="compact" />

              <Panel>
                <PanelHeader title={t('ov.snapshot')} subtitle={t('terms.sub')} />
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-background-200 pt-3 sm:grid-cols-2 lg:grid-cols-3">
                  {field(t('ov.discount'), partner.discount > 0 ? `${partner.discount}%` : t('ov.notDiscounted'))}
                  {field(t('ov.special'), partner.noDiscountOnSpecial ? t('ov.notDiscounted') : t('ov.discounted'))}
                  {field(t('pc.markups'), `B2C ${partner.markup ?? data.summary.markupB2c}% · B2B ${partner.markupB2b ?? data.summary.markupB2b}%`)}
                  {field(t('onb.deliveryDays'), partner.deliveryDaysMin != null || partner.deliveryDaysMax != null ? `${partner.deliveryDaysMin ?? '?'}–${partner.deliveryDaysMax ?? '?'}` : null)}
                  {field(t('onb.warrantyMonths'), partner.warrantyMonths)}
                  {field(t('ov.reservation'), `${partner.reservationPeriodDays} ${t('pc.days')} · ${partner.reservationRespondHours} ${t('pc.hours')}`)}
                  {field(t('onb.poConfirmHours'), partner.poConfirmHours)}
                  {field(t('onb.paymentLagDays'), partner.paymentLagDays)}
                  {field(t('ov.minResale'), partner.minResaleType === 'none' ? t('onb.minResale.none') : `${t(`onb.minResale.${partner.minResaleType}`, partner.minResaleType)}${partner.minResaleValue != null ? ` · ${partner.minResaleValue}` : ''}`)}
                </div>
                {partner.notes && (
                  <div className="mt-4 border-t border-background-200 pt-3">
                    <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-foreground-400">{t('ov.notes')}</p>
                    <p className="mt-1 whitespace-pre-wrap text-[12.5px] leading-5 text-foreground-700">{partner.notes}</p>
                  </div>
                )}
              </Panel>
            </div>

            <Panel>
              <PanelHeader title={t('pc.readyTitle')} subtitle={t('pc.readySub')} />
              <ol className="divide-y divide-background-200 border-t border-background-200">
                {readiness.map((r, i) => (
                  <li key={r.key} className="flex items-start gap-3 py-2.5">
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${r.ok ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'}`}>{r.ok ? <i className="ri-check-line" /> : i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <Link to={r.to} className="block text-[12.5px] font-medium text-foreground-900 hover:text-primary-700">{t(`onb.steps.${r.key}`)}</Link>
                      <p className="text-[11px] text-foreground-500">{r.value}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <Link to={`/partners/${partner.id}/onboarding?step=preview`} className={`${btnPrimary} mt-4 w-full justify-center`}>
                <i className="ri-layout-grid-line text-[14px]" /> {t('pc.openPreview')}
              </Link>
              {!data.summary.siteConfigured && <p className="mt-3 text-[11px] leading-4 text-accent-800">{t('pc.siteNotConfigured')}</p>}
            </Panel>
          </div>
        )}

        {tab === 'listings' && (
          <Panel padded={false}>
            <PanelHeader title={t('pc.tabs.listings')} subtitle={t('pc.listingsSub', { b2c: data.summary.markupB2c, b2b: data.summary.markupB2b })} />
            <div className="overflow-x-auto thin-scroll">
              <table className="w-full min-w-[900px] border-collapse text-[12px]">
                <thead>
                  <tr className="border-y border-background-200 bg-background-100/70 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                    <th className="px-4 py-2 font-medium">{t('pc.colSku')}</th>
                    <th className="px-3 py-2 font-medium">{t('pc.colName')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pc.colCost')}</th>
                    <th className="px-3 py-2 text-right font-medium">B2C</th>
                    <th className="px-3 py-2 text-right font-medium">B2B</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pc.colStock')}</th>
                    <th className="px-3 py-2 font-medium">{t('pc.colAvail')}</th>
                    <th className="px-4 py-2 font-medium">{t('pc.colListing')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: ListingRow) => (
                    <tr key={r.listing.sku} className="border-b border-background-200/70 last:border-0">
                      <td className="px-4 py-2 align-top">
                        <div className="flex items-center gap-2">
                          {r.listing.images[0] ? <img src={r.listing.images[0].url} alt="" className="h-8 w-8 rounded border border-background-200 bg-white object-contain" loading="lazy" /> : <span className="flex h-8 w-8 items-center justify-center rounded bg-background-100 text-[10px] text-foreground-400">—</span>}
                          <Link to={`/catalog/${encodeURIComponent(r.listing.sku)}`} className="tnum font-medium text-primary-700">{r.listing.sku}</Link>
                        </div>
                      </td>
                      <td className="max-w-[320px] px-3 py-2 align-top text-foreground-900">
                        <p className="truncate">{r.listing.name.en || t('pc.noName')}</p>
                        {r.problems.length > 0 && <p className="truncate text-[10.5px] text-rose-600">{r.problems.map((p) => t(`onb.problems.${p}`, p)).join(', ')}</p>}
                      </td>
                      <td className="tnum px-3 py-2 text-right align-top text-foreground-600">{fmtAed(r.prices.cost)}</td>
                      <td className={`tnum px-3 py-2 text-right align-top font-medium ${r.prices.b2cManual ? 'text-accent-800' : 'text-foreground-900'}`}>{fmtAed(r.prices.b2c)}</td>
                      <td className={`tnum px-3 py-2 text-right align-top font-medium ${r.prices.b2bManual ? 'text-accent-800' : 'text-foreground-900'}`}>{fmtAed(r.prices.b2b)}</td>
                      <td className="tnum px-3 py-2 text-right align-top text-foreground-700">{r.listing.availability.qty}</td>
                      <td className="px-3 py-2 align-top"><StatusBadge tone={toneOfAvail(r.listing.availability.state)}>{t(`pc.avail.${r.listing.availability.state}`, r.listing.availability.state)}</StatusBadge></td>
                      <td className="px-4 py-2 align-top">
                        <div className="flex items-center gap-2">
                          <StatusBadge tone={toneOfListing(r.listingStatus)} dot={false}>{t(`onb.status.${r.listingStatus}`)}</StatusBadge>
                          {r.siteUrl && <a href={r.siteUrl} target="_blank" rel="noreferrer" className="text-primary-700" title={r.siteUrl}><i className="ri-external-link-line text-[13px]" /></a>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                        {t('pc.noListings')} <Link to={`/import?partner=${partner.id}`} className="font-semibold text-primary-700">{t('onb.plOpenImport')}</Link>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-background-200 px-4 py-2.5 text-[11px] text-foreground-400">
              <span>{t('pc.listingsFooter', { total: rows.length, ready: data.summary.ready, live })}</span>
              <Link to={`/partners/${partner.id}/onboarding?step=preview`} className="font-semibold text-primary-700">{t('pc.openPreview')}</Link>
            </div>
          </Panel>
        )}

        {tab === 'site' && <SitePanel partner={partner} canEdit={Boolean(canEdit)} onPartner={onPartner} onToast={onToast} onApplied={() => void load()} mode="full" />}
        {tab === 'lists' && <PriceListStep partner={partner} />}
        {tab === 'terms' && (canEdit ? <CabinetStep partner={partner} onSaved={onPartner} onToast={onToast} /> : <p className="text-[12.5px] text-foreground-500">{t('pc.readOnly')}</p>)}
        {tab === 'contract' && <ContractStep partner={partner} onPartner={onPartner} onToast={onToast} canEdit={Boolean(canEdit)} />}

        {tab === 'orders' && (
          <Panel padded={false}>
            <PanelHeader title={t('pc.tabs.orders')} subtitle={t('ord.footerZoho')} />
            <div className="overflow-x-auto thin-scroll">
              <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="border-y border-background-200 bg-background-100/70 text-left text-[10.5px] uppercase tracking-[0.08em] text-foreground-500">
                    <th className="px-4 py-2 font-medium">{t('po.colPo')}</th>
                    <th className="px-3 py-2 font-medium">{t('ord.colReference')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('po.colLines')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('ord.colTotal')}</th>
                    <th className="px-3 py-2 font-medium">{t('ord.colOrdered')}</th>
                    <th className="px-4 py-2 font-medium">{t('po.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(orders ?? []).map((po) => (
                    <tr key={po.poNumber} className="border-b border-background-200/70 last:border-0">
                      <td className="px-4 py-2.5"><Link to={`/purchase-orders/${encodeURIComponent(po.poNumber)}`} className="tnum font-semibold text-primary-700">{po.poNumber}</Link></td>
                      <td className="max-w-[260px] truncate px-3 py-2.5 text-foreground-600">{po.reference || '—'}</td>
                      <td className="tnum px-3 py-2.5 text-right text-foreground-700">{po.lines}</td>
                      <td className="tnum px-3 py-2.5 text-right font-semibold text-foreground-950">{aed(po.total)}</td>
                      <td className="tnum px-3 py-2.5 text-foreground-600">{po.orderedAt ? shortDate(po.orderedAt) : '—'}</td>
                      <td className="px-4 py-2.5"><StatusBadge tone={poTone(po.fulfillmentStatus)}>{t(`status.${po.fulfillmentStatus}`, po.fulfillmentStatus)}</StatusBadge></td>
                    </tr>
                  ))}
                  {orders && orders.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-[12px] text-foreground-500">{t('pc.noOrders')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        )}
      </div>

      {toast && <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} />}
    </div>
  );
}
